import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { API_BASE, testIds } from "@/lib/api";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const rzpPaid = params.get("rzp") === "1";
  const [state, setState] = useState({ status: "polling", data: null, message: "Confirming your payment…" });

  useEffect(() => {
    if (rzpPaid) {
      setState({
        status: "paid",
        data: { order_type: params.get("type") || "bootcamp", amount: params.get("amount"), currency: "inr" },
        message: "Payment confirmed!",
      });
      return;
    }
    if (!sessionId) {
      setState({ status: "error", data: null, message: "Missing session id." });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await axios.get(`${API_BASE}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setState({ status: "paid", data: r.data, message: "Payment confirmed!" });
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "failed") {
          setState({ status: "failed", data: r.data, message: "Payment did not complete." });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data: r.data, message: "Still processing. We'll email you once confirmed." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setState({ status: "error", data: null, message: "Unable to verify payment. Please contact us." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isPaid = state.status === "paid";
  const isPending = state.status === "polling";

  return (
    <div
      data-testid={testIds.payment.successRoot}
      className="min-h-screen flex items-center justify-center px-6 bg-[#F7F5F0]"
    >
      <div className="max-w-lg w-full bg-white border border-[#E5E2D9] rounded-3xl p-10 md:p-14 text-center shadow-sm">
        <div className="flex justify-center mb-6">
          {isPending && <Loader2 className="w-14 h-14 text-[#89937C] animate-spin" />}
          {isPaid && <CheckCircle2 className="w-14 h-14 text-[#89937C]" />}
          {!isPending && !isPaid && <XCircle className="w-14 h-14 text-[#AD6A48]" />}
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl font-medium tracking-tight mb-4">
          {isPaid ? "You're in." : isPending ? "One moment…" : "Something's off"}
        </h1>
        <p data-testid={testIds.payment.statusText} className="text-[#5C665A] leading-relaxed mb-8">
          {isPaid
            ? state.data?.order_type === "coffee"
              ? "Your beans are on the way. Your bag will be roasted to order and shipped within 48 hours."
              : "Your spot is reserved. Coach Sanjay will reach out within 24 hours with what to expect and how to prepare."
            : state.message}
        </p>
        {state.data && isPaid && (
          <div className="text-sm text-[#5C665A] mb-8">
            Amount charged:{" "}
            <span className="font-medium text-[#2D372B]">
              {String(state.data.currency).toLowerCase() === "inr" ? "₹" : "$"}
              {state.data.amount} {String(state.data.currency).toUpperCase()}
            </span>
          </div>
        )}
        <Link
          to="/"
          data-testid={testIds.payment.successBackHome}
          className="inline-block rounded-full px-8 py-3 bg-[#89937C] text-white hover:bg-[#737D67] transition-colors font-medium"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
